import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DrawingDocument } from '../generated/drawing-contract';
import { toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import {
  ALL_REACTION_CATEGORIES,
  searchMarkush,
  searchMolecules,
  type ReactionCategory,
  type SearchResults
} from '@peruselab/api-client/moleculeSearch';
import type { MarkushDrawingDocument } from '@peruselab/structure-editor/domain/markushDocument';
import { describeMarkushChoices } from '@peruselab/structure-editor/domain/markushSummary';
import { toggleInSet } from '../components/panels/searchResultsModel';
import { useStructuralConstraints } from './useStructuralConstraints';
import type { SearchRequestState } from './searchRequestState';
import { mergeSearchResults, queryMolecules, type QueryMolecule } from './queryMolecules';
import { useActiveTenant } from '../tenancy/activeTenant';

/** Answers kept per molecule (and filter settings) -- enough for a drawing's worth of edits. */
const MAX_CACHED_ANSWERS = 200;
/** How long an answer is reused: long enough that edits and filter toggles don't re-ask, short
 *  enough that a drawing saved meanwhile shows up in the next search. */
const ANSWER_TTL_MS = 60_000;

const DEBOUNCE_MS = 500;

/** #658: a Markush match's results carry, per molecule id, which structure matched it (display
 *  text such as `R1 = F · n = 2`), for the result cards. */
export type MoleculeSearchResults = SearchResults & {
  readonly matchedByText?: Readonly<Record<string, string>>;
  /** #1178 (owner, 2026-11-02): for each molecule Identical to a molecule on the paper, that piece
   *  of the paper -- its card is drawn from it, so card, paper and exports look the same. */
  readonly identicalPieces?: Readonly<Record<string, DrawingDocument>>;
};

type MoleculeSearchState = SearchRequestState<MoleculeSearchResults>;

// Fires independently of chemistry validation (useChemicalValidation), not gated on it. The
// server's MoleculeSchemaValidator only checks contract shape (graph well-formedness, atomic
// numbers, bond orders), not chemical valence -- so a mid-edit structure RDKit would flag as
// chemically invalid can still be schema-valid and worth searching (e.g. checking whether a
// similar, already-valid structure exists while fixing a mistake). Gating on validation success
// would couple two independently designed features for no requirement either #110 or #117 states.
// The one gate that does apply: an empty graph is never searched (#106/#108's empty-structure
// policy -- "a valid editable document, not a registered molecule").
// #658 (Markush M4): with `markush` set -- a Markush drawing that is bounded, complete and within
// the cap -- the search is a Markush match on that drawing instead (the caller decides; otherwise
// `document`, the drawing's concrete core, is searched as before).
// #701: `document` is searched molecule by molecule (queryMolecules) -- each molecule on the paper,
// loose or in a reaction box, is its own query -- and the answers are merged, each result labelled
// with which molecule(s) it matched. An answer is kept per molecule, so editing one molecule
// re-asks for that one only; moving molecules or editing another asks for nothing new.
export function useMoleculeSearch(
  document: DrawingDocument,
  threshold?: number,
  markush?: { readonly document: MarkushDrawingDocument } | null
) {
  const [search, setSearch] = useState<MoleculeSearchState>({ state: 'idle' });
  const [lastAnswer, setLastAnswer] = useState<{ context: string; results: MoleculeSearchResults } | null>(null);
  // Server-side filter (ADR 0008 R4, #563: independent Category checkboxes): confine reaction
  // hits to any of several participant-role categories. Changing it re-fires the (debounced)
  // search, unlike the client-only result filters. Defaults to all three checked -- equivalent
  // to no restriction, matching the pre-#563 'ANY' default.
  const [reactionBuckets, setReactionBuckets] = useState<ReadonlySet<ReactionCategory>>(
    new Set(ALL_REACTION_CATEGORIES)
  );
  // #566: Cycle Sizes / Atom Symbols repeatable-row state, also server-side (#564/#565).
  const structuralConstraintRows = useStructuralConstraints();
  const { structuralConstraints } = structuralConstraintRows;
  const searchRequestRef = useRef(0);
  const { t } = useTranslation();
  const markushDocument = markush?.document ?? null;
  const queries = useMemo(() => (markushDocument ? [] : queryMolecules(document)), [document, markushDocument]);
  const answers = useRef(new Map<string, { readonly at: number; readonly answer: Promise<SearchResults> }>());
  // #1051: what the active tenant can see is part of the question -- switching asks afresh.
  const { scopeKey } = useActiveTenant();
  const filterKey = JSON.stringify([scopeKey, threshold ?? null, [...reactionBuckets].sort(), structuralConstraints]);
  // What is being asked: the molecules' chemistry and the filters -- not where they are drawn, so
  // moving a molecule (or resizing the paper) is not a new question.
  const displayContext = JSON.stringify([document.structure.graph.id, filterKey, markushDocument !== null]);
  const questionKey = markushDocument
    ? `markush:${JSON.stringify(markushDocument)}|${filterKey}`
    : `molecules:${queries.map((query) => query.key).join('|')}|${filterKey}`;
  const sourceKey = `${document.structure.graph.id}|${questionKey}`;
  /** One molecule's answer: reused when the same molecule was asked with the same filters. */
  const answerFor = useCallback(
    (query: QueryMolecule, fresh: boolean) => {
      const key = `${filterKey}|${query.key}`;
      const cached = answers.current.get(key);
      if (cached && !fresh && Date.now() - cached.at < ANSWER_TTL_MS) return cached;
      if (answers.current.size >= MAX_CACHED_ANSWERS) answers.current.clear();
      const pending = searchMolecules(
        toChemMoleculeV1(query.document),
        threshold,
        reactionBuckets,
        structuralConstraints
      );
      pending.catch(() => answers.current.delete(key)); // a failed answer is asked again next time
      const entry = { at: Date.now(), answer: pending };
      answers.current.set(key, entry);
      return entry;
    },
    [filterKey, reactionBuckets, structuralConstraints, threshold]
  );

  const currentResults = search.state === 'ready' && search.sourceKey === sourceKey ? search.results : null;
  // Keep presentation stable while a new answer arrives, without treating the previous answer
  // as a match for the current chemistry. Never carry it across document/filter/tenant changes.
  const displayResults =
    currentResults ??
    (document.structure.graph.nodes.length > 0 && lastAnswer?.context === displayContext ? lastAnswer.results : null);

  // When the answers on screen were fetched (the oldest of them), and whether they have outlived
  // ANSWER_TTL_MS -- then the match count offers a refresh (owner, #701: only once they expire).
  const [answeredAt, setAnsweredAt] = useState<number | null>(null);
  const [expiredAt, setExpiredAt] = useState<number | null>(null);
  useEffect(() => {
    if (answeredAt === null) return;
    const timeout = globalThis.setTimeout(
      () => setExpiredAt(answeredAt),
      Math.max(0, answeredAt + ANSWER_TTL_MS - Date.now())
    );
    return () => globalThis.clearTimeout(timeout);
  }, [answeredAt]);
  const resultsExpired = currentResults !== null && answeredAt !== null && expiredAt === answeredAt;
  const searchIsStale =
    search.state === 'ready'
      ? search.sourceKey !== sourceKey
      : search.state === 'error' && search.sourceKey !== sourceKey;

  /** `fresh`: ask the server again even for molecules with a recent answer (an explicit re-run). */
  const runSearch = useCallback(
    async (fresh = false) => {
      if (document.structure.graph.nodes.length === 0) {
        setSearch({ state: 'idle' });
        return;
      }
      const requestId = searchRequestRef.current + 1;
      searchRequestRef.current = requestId;
      const requestedSourceKey = sourceKey;
      setSearch({ state: 'loading', sourceKey: requestedSourceKey });
      try {
        let results: MoleculeSearchResults;
        let fetchedAt = Date.now();
        if (markushDocument) {
          const markushResults = await searchMarkush(markushDocument, reactionBuckets, structuralConstraints);
          results = {
            ...markushResults,
            matchedByText: Object.fromEntries(
              Object.entries(markushResults.matchedBy).map(([moleculeId, choices]) => [
                moleculeId,
                describeMarkushChoices(markushDocument, choices)
              ])
            )
          };
        } else {
          const entries = queries.map((query) => ({ query, entry: answerFor(query, fresh) }));
          fetchedAt = Math.min(fetchedAt, ...entries.map(({ entry }) => entry.at));
          const parts = await Promise.all(
            entries.map(async ({ query, entry }) => ({
              formula: query.formula,
              document: query.document,
              results: await entry.answer
            }))
          );
          results = {
            ...mergeSearchResults(parts, (formulas) =>
              t('searchResultsPanel.matchedQuery', { formulas: formulas.join(' · ') })
            ),
            identicalPieces: identicalPieces(parts)
          };
        }
        if (searchRequestRef.current === requestId) {
          setSearch({ state: 'ready', sourceKey: requestedSourceKey, results });
          setLastAnswer({ context: displayContext, results });
          setAnsweredAt(fetchedAt);
        }
      } catch (error) {
        if (searchRequestRef.current === requestId) {
          setSearch({
            state: 'error',
            sourceKey: requestedSourceKey,
            message: error instanceof Error ? error.message : 'Molecule search failed.'
          });
        }
      }
    },
    [
      answerFor,
      displayContext,
      document,
      markushDocument,
      queries,
      reactionBuckets,
      sourceKey,
      structuralConstraints,
      t
    ]
  );

  // The latest runSearch, for the debounced trigger below -- which fires on a new question
  // (sourceKey), not on every new `document` (a move changes the document, not the question).
  const latestRunSearch = useRef(runSearch);
  useEffect(() => {
    latestRunSearch.current = runSearch;
  });
  useEffect(() => {
    if (document.structure.graph.nodes.length === 0) {
      searchRequestRef.current += 1;
      setSearch({ state: 'idle' });
      setLastAnswer(null);
      return;
    }
    const timeout = globalThis.setTimeout(() => void latestRunSearch.current(false), DEBOUNCE_MS);
    // Cleanup cancels the pending timeout on every re-render this effect's deps change for --
    // including on unmount -- which is how a superseded edit's stale trigger gets dropped before
    // it ever fires. A response already in flight when a newer edit lands is separately guarded
    // by the requestId check in runSearch above, so out-of-order network resolution can't clobber
    // a newer result either.
    return () => globalThis.clearTimeout(timeout);
  }, [document.structure.graph.nodes.length, sourceKey]);

  // An explicit re-run -- the Retry link after a failed search (SearchStatusHeader), or the match
  // count's refresh once the results have expired: always asks the server afresh.
  const retry = useCallback(() => runSearch(true), [runSearch]);

  const toggleReactionBucket = useCallback(
    (category: ReactionCategory) => setReactionBuckets((current) => toggleInSet(current, category)),
    []
  );

  return {
    search,
    currentResults,
    displayResults,
    searchIsStale,
    runSearch: retry,
    /** The results shown are older than ANSWER_TTL_MS: `runSearch` would fetch them afresh. */
    resultsExpired,
    reactionBuckets,
    toggleReactionBucket,
    ...structuralConstraintRows
  };
}

/** #1178: each molecule Identical to a piece of the paper, with that piece (the first, if several). */
function identicalPieces(
  parts: readonly { readonly document: DrawingDocument; readonly results: SearchResults }[]
): Record<string, DrawingDocument> {
  const pieces: Record<string, DrawingDocument> = {};
  for (const { document, results } of parts) {
    const identical = [
      ...results.molecules.filter((match) => match.category === 'IDENTICAL').map((match) => match.moleculeId),
      ...[...results.drawings, ...results.reactions, ...results.illustrations]
        .filter((match) => match.category === 'IDENTICAL' && match.sourceMoleculeId)
        .map((match) => match.sourceMoleculeId!)
    ];
    for (const moleculeId of identical) pieces[moleculeId] ??= document;
  }
  return pieces;
}
